import type { AgMultiFilterModel } from "~/helpers/ag-grid/agFilterModels";
import type { ProductionScheduleSsrmRow } from "~produce/production/schedule/helpers/productionScheduleSsrmRow";

import { type GridRequestInput, ProducePlanningEntryUnit, ProductionLotStatus } from "~/gql/graphqlBase.generated";
import { isMultiModel, isSetModel, isTextModel } from "~/helpers/ag-grid/agFilterModels";
import { activeSubmodels, composeActiveSubmodels } from "~/helpers/ag-grid/unwrapMultiFilterModel";
import archivedRecordsScope from "~/helpers/grid/archivedRecordsScope";
import { toFilterModelRecord } from "~produce/helpers/withSiteLocationScope";
import { isStepNFilterValue } from "~produce/production/schedule/helpers/jobStep";

const PARENT_COL = "parentProductionJobId";
const JOB_ID_COL = "productionJobId";
const AUTO_GROUP_COL_ID = "ag-Grid-AutoColumn";
const STEP_COL_ID = "step";

// wsapi's root-scope fast paths match only a NULL set filter on parentProductionJobId, so the top level uses that form.
const ROOT_SCOPE_FILTER = { [PARENT_COL]: { filterType: "set", values: [null] } };

// wsapi compiles a notBlank text filter to parent not in [null, ""], so only start jobs come back.
const START_ONLY_FILTER = { [PARENT_COL]: { filterType: "text", type: "notBlank" } };

// Mirrors wsapi GridRegistry.cs BuildProductionScheduleConfig; auto-group/jobCode/jobStatus map to backend cols.
export const COL_ID_ALIASES: Record<string, string> = {
	[AUTO_GROUP_COL_ID]: "lotCode",
	jobCode: "jobNumber",
	jobStatus: "jobExecutionStatus"
};

export const LOT_CODE_QUERY_PARAM = "lotCode";

// Mirrors wsapi GridRegistry.cs BuildProductionScheduleConfig sortable columns; unlisted colIds are stripped.
export const SORTABLE_COL_IDS = new Set([
	"categoryName",
	"color",
	"customerGroupName",
	"customerName",
	"eaches",
	"genus",
	"isPreFinished",
	"jobNumber",
	"locationName",
	"lotCode",
	"noMaterialEvents",
	"noRecipeAtSite",
	"noTaskEvents",
	"originalCatalog",
	"originalWeekReadyIsoWeek",
	"originalWeekStartIsoWeek",
	"parentLocationName",
	"producedQuantity",
	"productionPlanName",
	"productionTime",
	"quantity",
	"requestedEaches",
	"series",
	"siteName",
	"stepNumber",
	"uomCode",
	"uomLabel",
	"weekReadyIsoWeek",
	"weekStartIsoWeek",
	"yield"
]);

// Mirrors wsapi GridRegistry.cs BuildProductionScheduleConfig FilterableCols; unlisted colIds are stripped.
export const FILTERABLE_COL_IDS = new Set([
	"categoryId",
	"categoryName",
	"color",
	"customerGroupId",
	"customerGroupName",
	"customerId",
	"customerName",
	"eaches",
	"genus",
	"isPreFinished",
	"jobExecutionStatus",
	"jobNumber",
	"locationId",
	"locationName",
	"lotCode",
	"lotStatus",
	"noMaterialEvents",
	"noRecipeAtSite",
	"noTaskEvents",
	"orderIds",
	"originalCatalog",
	"originalWeekReadyIsoWeek",
	"originalWeekStartIsoWeek",
	"parentLocationName",
	"producedQuantity",
	"productionJobId",
	"productionPlanId",
	"productionPlanName",
	"quantity",
	"requestedEaches",
	"series",
	"siteName",
	"step",
	"uomCode",
	"uomLabel",
	"weekReadyIsoWeek",
	"weekReadyIsoYear",
	"weekStartIsoWeek",
	"yield"
]);

// wsapi GridRegistry SetValueCols projects set values off the ID cols, not the Name cols.
export const SET_VALUE_ID_COL: Readonly<Record<string, string>> = {
	categoryName: "categoryId",
	customerGroupName: "customerGroupId",
	customerName: "customerId",
	locationName: "locationId",
	productionPlanName: "productionPlanId"
};

// Inverse of deriveJobStatus: a null execution status displays as "Scheduled".
export const JOB_STATUS_TO_EXECUTION_STATUSES: Record<string, (null | string)[]> = {
	Completed: ["EXECUTED"],
	"In Progress": ["EXECUTING"],
	Scheduled: ["PENDING", null]
};

const translateJobStatusFilter = (model: unknown): unknown => {
	if (!isSetModel(model) || !model.values) return model;
	const values = model.values.flatMap((value) =>
		value != null && value in JOB_STATUS_TO_EXECUTION_STATUSES ? JOB_STATUS_TO_EXECUTION_STATUSES[value] : []
	);
	return { ...model, values: [...new Set(values)] };
};

const redirectSetHalfToIdCol = (colId: string, idColId: string, model: AgMultiFilterModel): Record<string, unknown> => {
	const textModel = model.filterModels.find(isTextModel);
	const setModel = model.filterModels.find(isSetModel);
	return {
		...(textModel ? { [colId]: textModel } : {}),
		...(setModel ? { [idColId]: setModel } : {})
	};
};

const redirectOrComposeSubmodels = (colId: string, model: AgMultiFilterModel): Record<string, unknown> => {
	const idColId = SET_VALUE_ID_COL[colId];
	return idColId
		? redirectSetHalfToIdCol(colId, idColId, model)
		: composeActiveSubmodels(colId, activeSubmodels(model));
};

// wsapi's "eaches" colId (quantity x category eaches) is what the eaches-mode quantity cell shows.
const QUANTITY_COL_ID = "quantity";
const EACHES_COL_ID = "eaches";

const resolveColId = (colId: string, planningEntryUnit: ProducePlanningEntryUnit): string => {
	const aliased = COL_ID_ALIASES[colId] ?? colId;
	return aliased === QUANTITY_COL_ID && planningEntryUnit === ProducePlanningEntryUnit.Eaches ? EACHES_COL_ID : aliased;
};

// The Step column sorts on wsapi's stepNumber (its own "step" colId is the filter's array of values).
const SORT_COL_ALIASES: Record<string, string> = { step: "stepNumber" };

export const translateSortModel = <T extends { colId: string }>(
	sortModel: T[],
	planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
): T[] =>
	sortModel
		.map((item) => ({ ...item, colId: SORT_COL_ALIASES[item.colId] ?? resolveColId(item.colId, planningEntryUnit) }))
		.filter((item) => SORTABLE_COL_IDS.has(item.colId))
		// wsapi keeps the last direction for a repeated field, so keep the first (the one the header shows).
		.filter((item, index, all) => all.findIndex((other) => other.colId === item.colId) === index);

// Two grid columns can resolve to one wsapi colId (quantity and eaches in eaches mode); AND them rather than overwrite.
const mergeFilterEntries = (translated: Record<string, unknown>, entries: Record<string, unknown>): void => {
	for (const [colId, model] of Object.entries(entries)) {
		if (colId in translated) Object.assign(translated, composeActiveSubmodels(colId, [translated[colId], model]));
		else translated[colId] = model;
	}
};

const translateFilterModel = (
	filterModel: unknown,
	planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
): Record<string, unknown> | null => {
	if (!filterModel || typeof filterModel !== "object") return null;
	const translated: Record<string, unknown> = {};
	for (const [colId, model] of Object.entries(filterModel as Record<string, unknown>)) {
		const targetColId = resolveColId(colId, planningEntryUnit);
		if (!FILTERABLE_COL_IDS.has(targetColId)) continue;
		if (isParentScopedStepModel(targetColId, model)) continue;
		if (targetColId === "jobExecutionStatus")
			mergeFilterEntries(translated, { [targetColId]: translateJobStatusFilter(model) });
		else if (isMultiModel(model)) mergeFilterEntries(translated, redirectOrComposeSubmodels(targetColId, model));
		else mergeFilterEntries(translated, { [targetColId]: model });
	}
	return Object.keys(translated).length > 0 ? translated : null;
};

export const withRootScope = (filterModel: unknown, groupKeys: string[]): unknown => {
	if (groupKeys.length > 0) return filterModel ?? null;
	const model = (filterModel as Record<string, unknown> | null) ?? {};
	return JOB_ID_COL in model ? model : { ...model, ...ROOT_SCOPE_FILTER };
};

const withScheduleJobListScope = (filterModel: unknown, productionJobIds: readonly string[] | null): unknown =>
	productionJobIds
		? {
				...toFilterModelRecord(filterModel),
				[JOB_ID_COL]: { filterType: "set", values: [...productionJobIds] }
			}
		: filterModel;

const LOT_STATUS_COL = "lotStatus";
const LOT_LESS_JOB_STATUS = ProductionLotStatus.Unknown;

const VISIBLE_LOT_STATUSES_FILTER = {
	[LOT_STATUS_COL]: {
		filterType: "set",
		values: [ProductionLotStatus.Active, ProductionLotStatus.Draft, LOT_LESS_JOB_STATUS]
	}
};

export const withArchivedLotScope = (
	filterModel: unknown,
	showArchived: boolean,
	productionJobIds: readonly string[] | null = null
): unknown => {
	if (showArchived || productionJobIds) return filterModel;
	return archivedRecordsScope.scopeFilterModel(filterModel, showArchived, {
		hiddenStatuses: [ProductionLotStatus.Inactive],
		statusField: LOT_STATUS_COL,
		visibleStatuses: VISIBLE_LOT_STATUSES_FILTER[LOT_STATUS_COL].values
	});
};

const hasStepNValue = (model: unknown): boolean =>
	isSetModel(model) && (model.values ?? []).some((value) => isStepNFilterValue(value));

const isParentScopedStepModel = (targetColId: string, model: unknown): boolean =>
	targetColId === STEP_COL_ID && !hasStepNValue(model);

const listsStepNPickFlat = (filterModel: unknown): boolean =>
	hasStepNValue(toFilterModelRecord(filterModel)[STEP_COL_ID]);

const isStartOnlyStepFilter = (filterModel: unknown): boolean => {
	const model = toFilterModelRecord(filterModel)[STEP_COL_ID];
	return isSetModel(model) && model.values?.length === 1 && model.values[0] === "Start";
};

const isFinalOnlyStepFilter = (filterModel: unknown): boolean => {
	const model = toFilterModelRecord(filterModel)[STEP_COL_ID];
	return isSetModel(model) && model.values?.length === 1 && model.values[0] === "Final";
};

// Any productionJobId filter keeps wsapi's UnfilteredRowsScope off, so a flat export asks for "every job" explicitly.
const ANY_JOB_FILTER = { [JOB_ID_COL]: { filterType: "text", type: "notBlank" } };

const withExportParentScope = (translated: Record<string, unknown>, stepSource: unknown): Record<string, unknown> => {
	if (isStartOnlyStepFilter(stepSource)) return { ...translated, ...START_ONLY_FILTER };
	if (isFinalOnlyStepFilter(stepSource)) return { ...translated, ...ROOT_SCOPE_FILTER };
	return JOB_ID_COL in translated ? translated : { ...translated, ...ANY_JOB_FILTER };
};

const EXPORT_DEFAULT_SORT = [
	{ colId: "lotCode", sort: "asc" },
	{ colId: "jobNumber", sort: "asc" }
] as const;

export const isScheduleTreeGroup = (
	row: Pick<ProductionScheduleSsrmRow, "hasChildren">,
	filterModel: unknown
): boolean => row.hasChildren === true && !isStartOnlyStepFilter(filterModel) && !listsStepNPickFlat(filterModel);

export const buildRowGroupCols = (groupKeys: string[]): { field: string }[] =>
	groupKeys.map(() => ({ field: PARENT_COL }));

export const translateScheduleRequest = (
	request: GridRequestInput,
	planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
): GridRequestInput => ({
	...request,
	filterModel: isStartOnlyStepFilter(request.filterModel)
		? { ...translateFilterModel(request.filterModel, planningEntryUnit), ...START_ONLY_FILTER }
		: listsStepNPickFlat(request.filterModel)
			? translateFilterModel(request.filterModel, planningEntryUnit)
			: withRootScope(translateFilterModel(request.filterModel, planningEntryUnit), request.groupKeys),
	rowGroupCols: buildRowGroupCols(request.groupKeys),
	sortModel: translateSortModel(request.sortModel, planningEntryUnit)
});

export const buildScheduleRequestTransform =
	(
		productionJobIds: readonly string[] | null,
		getShowArchived: () => boolean = () => true,
		planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
	) =>
	(request: GridRequestInput): GridRequestInput =>
		translateScheduleRequest(
			{
				...request,
				filterModel: withArchivedLotScope(
					withScheduleJobListScope(request.filterModel, productionJobIds),
					getShowArchived(),
					productionJobIds
				)
			},
			planningEntryUnit
		);

export const buildScheduleExportRequestTransform =
	(
		productionJobIds: readonly string[] | null,
		getShowArchived: () => boolean = () => true,
		planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
	) =>
	(request: GridRequestInput): GridRequestInput => {
		const scoped = withArchivedLotScope(
			withScheduleJobListScope(request.filterModel, productionJobIds),
			getShowArchived(),
			productionJobIds
		);
		const sortModel = translateSortModel(request.sortModel, planningEntryUnit);
		return {
			...request,
			filterModel: withExportParentScope(translateFilterModel(scoped, planningEntryUnit) ?? {}, scoped),
			groupKeys: [],
			rowGroupCols: [],
			sortModel: sortModel.length > 0 ? sortModel : EXPORT_DEFAULT_SORT.map((item) => ({ ...item }))
		};
	};

export const buildExactJobRowRequest = (
	productionJobId: string,
	filterModel: unknown,
	showArchived = true,
	planningEntryUnit: ProducePlanningEntryUnit = ProducePlanningEntryUnit.Containers
): GridRequestInput =>
	buildScheduleRequestTransform(
		[productionJobId],
		() => true,
		planningEntryUnit
	)({
		endRow: 1,
		filterModel: withArchivedLotScope(filterModel, showArchived),
		groupKeys: [],
		rowGroupCols: [],
		sortModel: [],
		startRow: 0,
		valueCols: []
	});

export default translateFilterModel;

// Virtual filter the result link carries. Added after translation: translateFilterModel strips unknown columns.
export const BACKGROUND_JOB_FILTER_COL = "backgroundJobId";

export const withBackgroundJobScope = (filterModel: unknown, backgroundJobId: null | string): unknown => {
	if (!backgroundJobId) return filterModel;
	const model = filterModel && typeof filterModel === "object" ? (filterModel as Record<string, unknown>) : {};
	return {
		...model,
		[BACKGROUND_JOB_FILTER_COL]: { filterType: "set", values: [backgroundJobId] }
	};
};
